'use client';

import { useTranslations } from 'next-intl';
import { type ReactNode, useState } from 'react';
import { Icons } from '@/lib/ui-kit/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/ui-kit/popover';
import { cn } from '@/lib/ui-kit/utils';
import { EmojiPanel } from './EmojiPanel';
import { GifPanel } from './GifPanel';

type Tab = 'emoji' | 'gif';

interface EmojiGifPickerProps {
  // Emoji is inserted into the composer at the caret — the popover stays open
  // so several can be picked in a row.
  onSelectEmoji: (emoji: string) => void;
  // A GIF posts immediately (standalone comment), so the popover closes.
  onSelectGif: (id: string) => void;
  disabled?: boolean;
}

export const EmojiGifPicker = ({ onSelectEmoji, onSelectGif, disabled }: EmojiGifPickerProps) => {
  const t = useTranslations('gifPicker');
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('emoji');

  const handleGif = (id: string) => {
    onSelectGif(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={t('emojiButtonAria')}
          className="shrink-0 cursor-pointer rounded-[8px] p-1 text-v2-text-secondary transition-colors hover:text-v2-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Icons.EmojiSmile className="size-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[320px] overflow-hidden rounded-[10px] border-v2-border bg-v2-surface-secondary p-0"
      >
        <div className="px-4 pt-6">
          {tab === 'emoji' ? (
            <EmojiPanel onSelect={onSelectEmoji} />
          ) : (
            <GifPanel onSelect={handleGif} />
          )}
        </div>
        <div className="mt-2.5 flex items-center justify-center gap-2.5 border-t border-v2-border px-4 pt-3 pb-4">
          <TabButton active={tab === 'emoji'} onClick={() => setTab('emoji')}>
            {t('emojiTab')}
          </TabButton>
          <TabButton active={tab === 'gif'} onClick={() => setTab('gif')}>
            {t('gifTab')}
          </TabButton>
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}

const TabButton = ({ active, onClick, children }: TabButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex h-8 cursor-pointer items-center justify-center rounded-[10px] px-4 text-[13px] font-medium leading-4 tracking-[-0.13px] transition-colors',
      active
        ? 'bg-v2-btn-secondary-2 text-v2-text-primary'
        : 'text-v2-text-secondary hover:text-v2-text-primary',
    )}
  >
    {children}
  </button>
);
